// src/pages/admin/Dashboard.jsx
import { useNavigate } from "react-router-dom";
import { useAdmin } from "../../context/AdminContext";
import {
  Package,
  FolderKanban,
  Image,
  LogOut,
  Plus,
  Settings,
} from "lucide-react";

export default function Dashboard() {
  const navigate = useNavigate();
  const { admin, setAdmin } = useAdmin();

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    setAdmin(null);
    navigate("/admin/login");
  };

  const Card = ({ title, icon, children }) => (
    <div className="bg-white/10 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-xl hover:scale-[1.02] transition-all duration-200">
      <div className="flex items-center gap-2 mb-4 text-white">
        {icon}
        <h3 className="text-lg font-semibold">{title}</h3>
      </div>
      {children}
    </div>
  );

  const ActionButton = ({ label, icon, onClick, variant = "primary" }) => (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200
        ${
          variant === "primary"
            ? "bg-green-600 hover:bg-green-700 text-white"
            : "bg-white/10 hover:bg-white/20 text-white"
        }`}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-800 text-white">

      {/* Header */}
      <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 p-4 sm:p-6 border-b border-white/10 backdrop-blur-md">
        
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">
            Admin Dashboard
          </h1>
          <p className="text-gray-400 text-sm">
            Welcome back, {admin?.email || "Admin"}
          </p>
        </div>

        <button
          onClick={handleLogout}
          className="flex items-center gap-2 bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg text-sm font-medium transition"
        >
          <LogOut size={16} />
          Logout
        </button>
      </header>

      {/* Main */}
      <main className="p-4 sm:p-6 lg:p-10">

        <h2 className="text-xl font-semibold mb-6 text-gray-200">
          Management Panel
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

          {/* Products */}
          <Card title="Products" icon={<Package size={18} />}>
            <div className="space-y-3">
              <ActionButton
                label="Add Product"
                icon={<Plus size={16} />}
                onClick={() => navigate("/admin/manage-products/add")}
              />
              <ActionButton
                label="Manage Products"
                icon={<Settings size={16} />}
                variant="secondary"
                onClick={() => navigate("/admin/manage-products")}
              />
            </div>
          </Card>

          {/* Projects */}
          <Card title="Projects" icon={<FolderKanban size={18} />}>
            <div className="space-y-3">
              <ActionButton
                label="Add Project"
                icon={<Plus size={16} />}
                onClick={() => navigate("/admin/manage-projects/add")}
              />
              <ActionButton
                label="Manage Projects"
                icon={<Settings size={16} />}
                variant="secondary"
                onClick={() => navigate("/admin/manage-projects")}
              />
            </div>
          </Card>

          {/* Gallery */}
          <Card title="Gallery" icon={<Image size={18} />}>
            <div className="space-y-3">
              <ActionButton
                label="Add Image"
                icon={<Plus size={16} />}
                onClick={() => navigate("/admin/manage-gallery/add")}
              />
              <ActionButton
                label="Manage Gallery"
                icon={<Settings size={16} />}
                variant="secondary"
                onClick={() => navigate("/admin/manage-gallery")}
              />
            </div>
          </Card>

        </div>
      </main>
    </div>
  );
}